import { cardFullscreen } from "../cards/CardFullscreen";
import { createCardArt } from "../cards/cardFace";
import {
  copiesOwned,
  isOwned,
  loadCollection,
  MAX_COPIES,
  ownedVerseIds,
} from "../collection";
import {
  CATALOG,
  catalogById,
  DEFAULT_DECK_IDS,
  type CatalogVerse,
} from "./catalog";
import {
  createDeck,
  currentDeckBounds,
  deleteDeck,
  getActiveDeckId,
  listDecks,
  loadDeck,
  renameDeck,
  saveDeck,
  saveDeckAs,
  setActiveDeck,
  type NamedDeck,
} from "./storage";
import { keywordsForVerse } from "../../prototypes/proclaim/game/cards/keywords";
import { verseById } from "../../prototypes/proclaim/data/verses";
import { clearEndlessCheckpointsForDeck } from "../../prototypes/proclaim/game/endlessCheckpoint";

export interface DeckPickerOptions {
  root: HTMLElement;
  onChange?: (deck: NamedDeck) => void;
  /** Fired when selection or dirty state changes (even before save). */
  onDirtyChange?: (state: {
    dirty: boolean;
    valid: boolean;
    count: number;
    verseIds: string[];
    deckId: string;
    deckName: string;
  }) => void;
}

/**
 * Multi-select deck builder with a saved-deck library.
 * Selected loadout is a separate strip at the top; catalog below.
 * Multiple copies of a verse are allowed up to how many you own.
 */
export class DeckPicker {
  /** Multiset of verse ids in the current loadout (duplicates = extra copies). */
  private selected: string[] = [];
  /** Deck currently being edited (library id). */
  private editingId: string;
  private editingName: string;
  /** Last successfully saved selection (for dirty detection). */
  private savedSnapshot = "";
  private statusEl: HTMLElement;
  private loadoutEl: HTMLElement;
  private catalogEl: HTMLElement;
  private loadoutCountEl: HTMLElement;
  private catalogCountEl: HTMLElement;
  private countEl: HTMLElement;
  private btnSave: HTMLButtonElement;
  private compositionEl: HTMLElement;
  private librarySelect: HTMLSelectElement;
  private libraryMeta: HTMLElement;
  private lockedEl: HTMLElement | null = null;
  private lockedCountEl: HTMLElement | null = null;
  /** Status line next to sticky Save (always in view). */
  private actionStatusEl: HTMLElement | null = null;
  private saveFlashTimer = 0;

  private get min(): number {
    return currentDeckBounds().min;
  }

  private get max(): number {
    return currentDeckBounds().max;
  }

  constructor(private readonly opts: DeckPickerOptions) {
    loadCollection();
    const saved = loadDeck();
    this.editingId = saved.id;
    this.editingName = saved.name;
    // Drop unowned leftovers; keep multiplicity within owned caps
    this.selected = this.clampToOwned(saved.verseIds);
    this.savedSnapshot = this.snapshotOf(this.selected);

    opts.root.innerHTML = "";
    opts.root.classList.add("deck-picker");

    const head = document.createElement("div");
    head.className = "deck-picker-head";

    const title = document.createElement("h2");
    title.className = "sr-only";
    title.textContent = "Proclaim deck";

    // —— Saved decks library ——
    const library = document.createElement("div");
    library.className = "deck-library";
    library.setAttribute("aria-label", "Saved decks");

    const libLabel = document.createElement("label");
    libLabel.className = "deck-library-label";
    libLabel.htmlFor = "deck-library-select";
    libLabel.textContent = "Saved deck";

    this.librarySelect = document.createElement("select");
    this.librarySelect.id = "deck-library-select";
    this.librarySelect.className = "deck-library-select";
    this.librarySelect.addEventListener("change", () => {
      const id = this.librarySelect.value;
      if (!id || id === this.editingId) return;
      if (this.isDirty()) {
        const ok = window.confirm(
          "Switch deck? Unsaved changes to the current loadout will be lost.",
        );
        if (!ok) {
          this.librarySelect.value = this.editingId;
          return;
        }
      }
      this.loadLibraryDeck(id);
    });

    this.libraryMeta = document.createElement("span");
    this.libraryMeta.className = "deck-library-meta";

    const libActions = document.createElement("div");
    libActions.className = "deck-library-actions";

    const btnNew = document.createElement("button");
    btnNew.type = "button";
    btnNew.className = "btn secondary btn-sm";
    btnNew.textContent = "New";
    btnNew.title = "Create a new deck from the starter loadout";
    btnNew.addEventListener("click", () => this.onNewDeck());

    const btnSaveAs = document.createElement("button");
    btnSaveAs.type = "button";
    btnSaveAs.className = "btn secondary btn-sm";
    btnSaveAs.textContent = "Save as…";
    btnSaveAs.title = "Save the current loadout as a new named deck";
    btnSaveAs.addEventListener("click", () => this.onSaveAs());

    const btnRename = document.createElement("button");
    btnRename.type = "button";
    btnRename.className = "btn secondary btn-sm";
    btnRename.textContent = "Rename";
    btnRename.addEventListener("click", () => this.onRename());

    const btnDelete = document.createElement("button");
    btnDelete.type = "button";
    btnDelete.className = "btn secondary btn-sm";
    btnDelete.textContent = "Delete";
    btnDelete.addEventListener("click", () => this.onDelete());

    libActions.append(btnNew, btnSaveAs, btnRename, btnDelete);
    library.append(libLabel, this.librarySelect, this.libraryMeta, libActions);

    this.countEl = document.createElement("p");
    this.countEl.className = "deck-count";

    this.compositionEl = document.createElement("p");
    this.compositionEl.className = "deck-composition";

    const hint = document.createElement("p");
    hint.className = "deck-picker-hint";
    hint.textContent =
      "Only verses you own can go in a loadout — add up to as many copies as you own of each type. Earn more through campaign rewards (and later endless packs). Progress is saved on this device.";

    this.statusEl = document.createElement("p");
    this.statusEl.className = "deck-status";
    this.statusEl.setAttribute("role", "status");
    this.statusEl.setAttribute("aria-live", "polite");

    head.append(
      title,
      library,
      this.countEl,
      this.compositionEl,
      hint,
      this.statusEl,
    );

    // —— Loadout (selected) ——
    const loadoutSection = document.createElement("section");
    loadoutSection.className = "deck-section deck-section-loadout";
    loadoutSection.setAttribute("aria-label", "Your loadout");

    const loadoutHead = document.createElement("div");
    loadoutHead.className = "deck-section-head";
    const loadoutTitle = document.createElement("h3");
    loadoutTitle.className = "deck-section-title";
    loadoutTitle.textContent = "Your loadout";
    this.loadoutCountEl = document.createElement("span");
    this.loadoutCountEl.className = "deck-section-meta";
    loadoutHead.append(loadoutTitle, this.loadoutCountEl);

    this.loadoutEl = document.createElement("div");
    this.loadoutEl.className =
      "deck-catalog deck-catalog-faces deck-loadout-faces";
    this.loadoutEl.setAttribute("role", "list");

    loadoutSection.append(loadoutHead, this.loadoutEl);

    // —— Available ——
    const catalogSection = document.createElement("section");
    catalogSection.className = "deck-section deck-section-catalog";
    catalogSection.setAttribute("aria-label", "Available verses");

    const catalogHead = document.createElement("div");
    catalogHead.className = "deck-section-head";
    const catalogTitle = document.createElement("h3");
    catalogTitle.className = "deck-section-title";
    catalogTitle.textContent = "Available";
    this.catalogCountEl = document.createElement("span");
    this.catalogCountEl.className = "deck-section-meta";
    catalogHead.append(catalogTitle, this.catalogCountEl);

    this.catalogEl = document.createElement("div");
    this.catalogEl.className = "deck-catalog deck-catalog-faces";
    this.catalogEl.setAttribute("role", "list");

    catalogSection.append(catalogHead, this.catalogEl);

    // —— Locked (unowned) ——
    const lockedSection = document.createElement("section");
    lockedSection.className = "deck-section deck-section-locked";
    lockedSection.setAttribute("aria-label", "Locked verses");

    const lockedHead = document.createElement("div");
    lockedHead.className = "deck-section-head";
    const lockedTitle = document.createElement("h3");
    lockedTitle.className = "deck-section-title";
    lockedTitle.textContent = "Not yet owned";
    this.lockedCountEl = document.createElement("span");
    this.lockedCountEl.className = "deck-section-meta";
    lockedHead.append(lockedTitle, this.lockedCountEl);

    this.lockedEl = document.createElement("div");
    this.lockedEl.className = "deck-catalog deck-catalog-faces deck-catalog-locked";
    this.lockedEl.setAttribute("role", "list");
    lockedSection.append(lockedHead, this.lockedEl);

    const actions = document.createElement("div");
    actions.className = "deck-actions deck-actions-sticky";

    this.actionStatusEl = document.createElement("p");
    this.actionStatusEl.className = "deck-action-status";
    this.actionStatusEl.setAttribute("role", "status");
    this.actionStatusEl.setAttribute("aria-live", "polite");

    const btnDefault = document.createElement("button");
    btnDefault.type = "button";
    btnDefault.className = "btn secondary";
    btnDefault.textContent = "Reset to starter";
    btnDefault.addEventListener("click", () => {
      if (
        !window.confirm(
          "Restore the starter loadout on this deck? Unsaved edits will be lost.",
        )
      ) {
        return;
      }
      this.selected = this.clampToOwned(
        DEFAULT_DECK_IDS.filter((id) => isOwned(id)),
      );
      this.setStatus("Reset to starter — click Save deck to keep it.", false);
      this.renderList();
      this.syncChrome();
    });

    this.btnSave = document.createElement("button");
    this.btnSave.type = "button";
    this.btnSave.className = "btn primary";
    this.btnSave.textContent = "Save deck";
    this.btnSave.addEventListener("click", () => this.save());

    const actionBtns = document.createElement("div");
    actionBtns.className = "deck-actions-btns";
    actionBtns.append(btnDefault, this.btnSave);
    actions.append(this.actionStatusEl, actionBtns);
    opts.root.append(head, loadoutSection, catalogSection, lockedSection, actions);
    this.refreshLibrarySelect();
    this.renderList();
    this.syncChrome();

    window.addEventListener("beforeunload", (e) => {
      if (!this.isDirty()) return;
      e.preventDefault();
      e.returnValue = "";
    });
  }

  getSelectedIds(): string[] {
    return [...this.selected];
  }

  getEditingDeckId(): string {
    return this.editingId;
  }

  isDirty(): boolean {
    return this.snapshotOf(this.selected) !== this.savedSnapshot;
  }

  isValid(): boolean {
    const n = this.selected.length;
    return n >= this.min && n <= this.max;
  }

  /** Multiset snapshot (sorted so order does not affect dirty). */
  private snapshotOf(ids: readonly string[]): string {
    return [...ids].sort().join("\0");
  }

  /** How many copies of `id` are currently in the loadout. */
  private countInLoadout(id: string): number {
    let n = 0;
    for (const x of this.selected) if (x === id) n++;
    return n;
  }

  /**
   * Keep only owned ids, at most copiesOwned each, and at most deck max total.
   * Preserves relative order of accepted entries.
   */
  private clampToOwned(ids: readonly string[]): string[] {
    const out: string[] = [];
    const used: Record<string, number> = {};
    for (const raw of ids) {
      if (out.length >= this.max) break;
      if (!isOwned(raw)) continue;
      const owned = copiesOwned(raw);
      const n = used[raw] ?? 0;
      if (n >= owned) continue;
      used[raw] = n + 1;
      out.push(raw);
    }
    return out;
  }

  private refreshLibrarySelect(): void {
    const decks = listDecks();
    this.librarySelect.replaceChildren();
    for (const d of decks) {
      const opt = document.createElement("option");
      opt.value = d.id;
      opt.textContent = `${d.name} (${d.verseIds.length})`;
      if (d.id === this.editingId) opt.selected = true;
      this.librarySelect.appendChild(opt);
    }
    this.librarySelect.value = this.editingId;
    const active = getActiveDeckId();
    this.libraryMeta.textContent =
      this.editingId === active
        ? "Current loadout for endless"
        : "Select & save to use in endless";
  }

  private loadLibraryDeck(id: string): void {
    const deck = loadDeck(id);
    setActiveDeck(id);
    this.editingId = deck.id;
    this.editingName = deck.name;
    this.selected = this.clampToOwned(deck.verseIds);
    this.savedSnapshot = this.snapshotOf(this.selected);
    this.setStatus(`Loaded “${deck.name}”`, false);
    this.refreshLibrarySelect();
    this.renderList();
    this.syncChrome();
    this.opts.onChange?.(deck);
  }

  private onNewDeck(): void {
    if (this.isDirty()) {
      const ok = window.confirm(
        "Create a new deck? Unsaved changes to the current loadout will be lost.",
      );
      if (!ok) return;
    }
    const name = window.prompt("Name for the new deck:", "New deck");
    if (name == null) return;
    try {
      const deck = createDeck(name.trim() || "New deck");
      this.editingId = deck.id;
      this.editingName = deck.name;
      this.selected = this.clampToOwned(deck.verseIds);
      this.savedSnapshot = this.snapshotOf(this.selected);
      this.setStatus(`Created “${deck.name}”`, false);
      this.refreshLibrarySelect();
      this.renderList();
      this.syncChrome();
      this.opts.onChange?.(deck);
    } catch (e) {
      this.setStatus(
        e instanceof Error ? e.message : "Could not create deck.",
        true,
      );
    }
  }

  private onSaveAs(): void {
    if (!this.isValid()) {
      this.setStatus(
        `Need ${this.min}–${this.max} verses to save.`,
        true,
      );
      return;
    }
    const name = window.prompt(
      "Save current loadout as:",
      `${this.editingName} copy`,
    );
    if (name == null) return;
    try {
      const deck = saveDeckAs(name, [...this.selected]);
      this.editingId = deck.id;
      this.editingName = deck.name;
      this.savedSnapshot = this.snapshotOf(this.selected);
      this.setStatus(`Saved as “${deck.name}”`, false);
      this.flashSaveSuccess();
      this.refreshLibrarySelect();
      this.syncChrome();
      this.opts.onChange?.(deck);
    } catch (e) {
      this.setStatus(
        e instanceof Error ? e.message : "Could not save deck.",
        true,
      );
    }
  }

  private onRename(): void {
    const name = window.prompt("Rename deck:", this.editingName);
    if (name == null) return;
    try {
      // Persist verses first if dirty & valid so rename doesn't strand edits
      if (this.isDirty() && this.isValid()) {
        const prevId = this.editingId;
        saveDeck([...this.selected], this.editingId);
        this.savedSnapshot = this.snapshotOf(this.selected);
        clearEndlessCheckpointsForDeck(prevId);
      }
      const deck = renameDeck(this.editingId, name);
      if (!deck) throw new Error("Deck not found.");
      this.editingName = deck.name;
      this.setStatus(`Renamed to “${deck.name}”`, false);
      this.refreshLibrarySelect();
      this.syncChrome();
      this.opts.onChange?.(deck);
    } catch (e) {
      this.setStatus(
        e instanceof Error ? e.message : "Could not rename.",
        true,
      );
    }
  }

  private onDelete(): void {
    if (listDecks().length <= 1) {
      this.setStatus("Keep at least one deck.", true);
      return;
    }
    if (
      !window.confirm(
        `Delete “${this.editingName}”? This cannot be undone.`,
      )
    ) {
      return;
    }
    try {
      const goneId = this.editingId;
      const next = deleteDeck(this.editingId);
      clearEndlessCheckpointsForDeck(goneId);
      this.editingId = next.id;
      this.editingName = next.name;
      this.selected = this.clampToOwned(next.verseIds);
      this.savedSnapshot = this.snapshotOf(this.selected);
      this.setStatus(`Deleted. Now editing “${next.name}”.`, false);
      this.refreshLibrarySelect();
      this.renderList();
      this.syncChrome();
      this.opts.onChange?.(next);
    } catch (e) {
      this.setStatus(
        e instanceof Error ? e.message : "Could not delete.",
        true,
      );
    }
  }

  private setStatus(message: string, isError: boolean): void {
    this.statusEl.textContent = message;
    this.statusEl.classList.toggle("error", isError);
    if (this.actionStatusEl) {
      this.actionStatusEl.textContent = message;
      this.actionStatusEl.classList.toggle("error", isError);
      this.actionStatusEl.classList.toggle("ok", !isError && !!message);
    }
  }

  private flashSaveSuccess(): void {
    this.btnSave.classList.remove("is-saved-flash");
    // Restart animation if already playing
    void this.btnSave.offsetWidth;
    this.btnSave.classList.add("is-saved-flash");
    if (this.saveFlashTimer) window.clearTimeout(this.saveFlashTimer);
    this.saveFlashTimer = window.setTimeout(() => {
      this.btnSave.classList.remove("is-saved-flash");
      this.saveFlashTimer = 0;
    }, 1600);
  }

  private save(): void {
    const n = this.selected.length;
    const dirty = this.isDirty();

    if (!dirty) {
      this.setStatus(
        `“${this.editingName}” is already saved (${n} cards). Change the loadout to enable a new save.`,
        false,
      );
      this.btnSave.title = "No unsaved changes";
      return;
    }

    if (n < this.min) {
      this.setStatus(
        `Need at least ${this.min} cards to save (you have ${n}). Add more from Available.`,
        true,
      );
      return;
    }
    if (n > this.max) {
      this.setStatus(
        `At most ${this.max} cards allowed (you have ${n}). Remove some from the loadout.`,
        true,
      );
      return;
    }

    try {
      const deck = saveDeck([...this.selected], this.editingId);
      clearEndlessCheckpointsForDeck(deck.id);
      this.editingId = deck.id;
      this.editingName = deck.name;
      // Snapshot from the live selection (sorted) so dirty clears reliably
      this.savedSnapshot = this.snapshotOf(this.selected);
      // Also align if storage reordered/dropped anything
      if (this.snapshotOf(deck.verseIds) !== this.savedSnapshot) {
        this.selected = this.clampToOwned(deck.verseIds);
        this.savedSnapshot = this.snapshotOf(this.selected);
        this.renderList();
      }
      const msg = `Saved “${deck.name}” · ${deck.verseIds.length} cards · current endless loadout`;
      this.setStatus(msg, false);
      this.flashSaveSuccess();
      this.refreshLibrarySelect();
      this.opts.onChange?.(deck);
      this.syncChrome();
    } catch (e) {
      this.setStatus(
        e instanceof Error ? e.message : "Could not save deck.",
        true,
      );
    }
  }

  private emitDirty(): void {
    this.opts.onDirtyChange?.({
      dirty: this.isDirty(),
      valid: this.isValid(),
      count: this.selected.length,
      verseIds: [...this.selected],
      deckId: this.editingId,
      deckName: this.editingName,
    });
  }

  private syncChrome(): void {
    const n = this.selected.length;
    const dirty = this.isDirty();
    const valid = n >= this.min && n <= this.max;
    const kinds = ownedVerseIds().length;
    let totalOwned = 0;
    for (const id of ownedVerseIds()) totalOwned += copiesOwned(id);
    this.countEl.textContent = `“${this.editingName}” · ${n} / ${this.max} cards (min ${this.min}) · ${kinds} verses · ${totalOwned} owned${
      dirty ? " · unsaved" : " · saved"
    }`;
    this.countEl.classList.toggle("invalid", n < this.min || n > this.max);

    const faith = this.selected.filter(
      (id) => catalogById(id)?.category === "faith",
    ).length;
    const peace = this.selected.filter(
      (id) => catalogById(id)?.category === "peace",
    ).length;
    const wisdom = this.selected.filter(
      (id) => catalogById(id)?.category === "wisdom",
    ).length;
    this.compositionEl.textContent =
      n === 0
        ? ""
        : `Composition · Faith ${faith} · Peace ${peace} · Wisdom ${wisdom}`;

    this.loadoutCountEl.textContent =
      n === 0 ? "empty — pick from below" : `${n} card${n === 1 ? "" : "s"}`;
    const avail = this.orderedAvailable().length;
    const maxed = this.orderedMaxedOwned().length;
    if (avail === 0 && maxed === 0) {
      this.catalogCountEl.textContent = "none owned";
    } else if (avail === 0) {
      this.catalogCountEl.textContent = `all copies in loadout · ${maxed} type${maxed === 1 ? "" : "s"}`;
    } else {
      this.catalogCountEl.textContent =
        maxed > 0
          ? `${avail} addable · ${maxed} maxed`
          : `${avail} type${avail === 1 ? "" : "s"} with free copies`;
    }

    if (this.lockedCountEl) {
      const locked = this.orderedLocked().length;
      this.lockedCountEl.textContent =
        locked === 0 ? "collection complete" : `${locked} locked`;
    }

    // Keep the button clickable so we can explain why save is blocked
    this.btnSave.disabled = false;
    this.btnSave.classList.toggle("is-muted", !dirty || !valid);
    this.btnSave.textContent = dirty ? "Save deck*" : "Save deck";
    if (!dirty) {
      this.btnSave.title = "No unsaved changes — edit the loadout first";
    } else if (!valid) {
      this.btnSave.title =
        n < this.min
          ? `Need ${this.min - n} more card${this.min - n === 1 ? "" : "s"} before saving`
          : `Remove ${n - this.max} card${n - this.max === 1 ? "" : "s"} before saving`;
    } else {
      this.btnSave.title = `Save “${this.editingName}” as the current endless loadout`;
    }

    // Surface an unsaved hint next to Save when there are pending edits
    if (
      dirty &&
      valid &&
      this.actionStatusEl &&
      !this.actionStatusEl.classList.contains("error")
    ) {
      const pending = (this.actionStatusEl.textContent ?? "").trim();
      if (!pending || pending.startsWith("Saved") || pending.startsWith("Loaded")) {
        this.setStatus("Unsaved changes — click Save deck to keep them.", false);
      }
    }

    this.emitDirty();
  }

  /** One entry per copy in the loadout (duplicates allowed). */
  private orderedSelected(): CatalogVerse[] {
    const byId = new Map(CATALOG.map((v) => [v.id, v]));
    const out: CatalogVerse[] = [];
    for (const id of this.selected) {
      const v = byId.get(id);
      if (v) out.push(v);
    }
    return out;
  }

  /**
   * Owned verses that still have at least one free copy (owned > in loadout).
   * Fully stacked types stay visible with "Max copies" if you want to study them —
   * we keep them listed so ownership remains visible.
   */
  private orderedAvailable(): CatalogVerse[] {
    return CATALOG.filter((v) => {
      if (!isOwned(v.id)) return false;
      return this.countInLoadout(v.id) < copiesOwned(v.id);
    });
  }

  /**
   * Multi-copy owned types already fully stacked in the loadout.
   * Kept visible under Available so ownership remains obvious; singles are
   * omitted (they already appear once in the loadout strip).
   */
  private orderedMaxedOwned(): CatalogVerse[] {
    return CATALOG.filter((v) => {
      if (!isOwned(v.id)) return false;
      const owned = copiesOwned(v.id);
      return owned > 1 && this.countInLoadout(v.id) >= owned;
    });
  }

  private orderedLocked(): CatalogVerse[] {
    return CATALOG.filter((v) => !isOwned(v.id));
  }

  private renderList(): void {
    this.loadoutEl.innerHTML = "";
    this.catalogEl.innerHTML = "";
    if (this.lockedEl) this.lockedEl.innerHTML = "";

    const selected = this.orderedSelected();
    if (selected.length === 0) {
      const empty = document.createElement("p");
      empty.className = "deck-loadout-empty";
      empty.textContent =
        "No cards selected yet. Use Add under a verse you own to build your loadout — you can add multiple copies of the same verse if you own them.";
      this.loadoutEl.appendChild(empty);
    } else {
      // Track occurrence index so remove targets a specific copy
      const seen: Record<string, number> = {};
      for (const verse of selected) {
        const occ = seen[verse.id] ?? 0;
        seen[verse.id] = occ + 1;
        this.loadoutEl.appendChild(this.renderCard(verse, true, occ));
      }
    }

    const available = this.orderedAvailable();
    const maxed = this.orderedMaxedOwned();
    if (available.length === 0 && maxed.length === 0) {
      const empty = document.createElement("p");
      empty.className = "deck-loadout-empty";
      empty.textContent =
        ownedVerseIds().length === 0
          ? "No owned verses yet."
          : "All owned copies are in your loadout.";
      this.catalogEl.appendChild(empty);
    } else {
      for (const verse of available) {
        this.catalogEl.appendChild(this.renderCard(verse, false));
      }
      // Fully stacked types still show so ownership stays visible
      for (const verse of maxed) {
        this.catalogEl.appendChild(this.renderCard(verse, false));
      }
    }

    if (this.lockedEl) {
      const locked = this.orderedLocked();
      if (locked.length === 0) {
        const empty = document.createElement("p");
        empty.className = "deck-loadout-empty";
        empty.textContent = "You own every verse in the catalog.";
        this.lockedEl.appendChild(empty);
      } else {
        for (const verse of locked) {
          this.lockedEl.appendChild(this.renderLockedCard(verse));
        }
      }
    }
  }

  private buildGameFace(verse: CatalogVerse, selected: boolean): HTMLElement {
    const def = verseById(verse.id);
    const speak = def?.speakSeconds;
    const cost = speak != null ? Math.round(speak * 10) / 10 : undefined;

    const card = document.createElement("article");
    card.className = `card category-${verse.category} card-display card-size-deck clean-face`;
    if (selected) card.classList.add("selected");
    card.dataset.verseId = verse.id;
    card.setAttribute("aria-hidden", "true");

    const art = createCardArt({
      verseId: verse.id,
      category: verse.category,
      reference: verse.reference,
      speakSeconds: cost,
      compactRef: true,
    });
    card.append(art);
    return card;
  }

  private openStudy(verse: CatalogVerse): void {
    const def = verseById(verse.id);
    if (def) {
      const kws = keywordsForVerse(def);
      const speak = def.speakSeconds ?? 2.5;
      const detailRows: { label: string; value: string }[] = [
        { label: "Speak", value: `${speak.toFixed(1)}s` },
        { label: "Kind", value: def.kind },
        { label: "Category", value: def.category },
      ];
      if (def.strongVs?.length) {
        detailRows.push({
          label: "Meets",
          value: def.strongVs
            .map((t) => t.charAt(0).toUpperCase() + t.slice(1))
            .join(" / "),
        });
      }
      if (def.preferLayer) {
        detailRows.push({ label: "Layer", value: def.preferLayer });
      }
      if (def.placeable) {
        detailRows.push({
          label: "Prayer",
          value: `${def.placeable.name} · ${def.placeable.level}`,
        });
      }
      if (def.convertPrayerBonus != null) {
        detailRows.push({
          label: "Redeem",
          value: def.convertPrayerBonus >= 1 ? "Strong (+1)" : "Weak (+0)",
        });
      }
      cardFullscreen.show({
        verseId: def.id,
        reference: def.reference,
        category: def.category,
        speakSeconds: speak,
        metaLine: `${def.category} · ${def.kind}`,
        keywords: kws.map((k) => ({
          id: k.id,
          label: k.label,
          reminder: k.reminder,
        })),
        textKjv: def.textKjv,
        proclaimText: def.proclaimText,
        effectSummary: def.effectSummary,
        detailRows,
        studyLayout: true,
      });
      return;
    }

    cardFullscreen.show({
      verseId: verse.id,
      reference: verse.reference,
      category: verse.category,
      metaLine: verse.category,
      effectLine: verse.blurb,
      textKjv: verse.textKjv,
      effectSummary: verse.blurb,
      studyLayout: true,
    });
  }

  private renderLockedCard(verse: CatalogVerse): HTMLElement {
    const wrap = document.createElement("div");
    wrap.className = "deck-card-slot is-locked";
    wrap.setAttribute("role", "listitem");

    const faceBtn = document.createElement("button");
    faceBtn.type = "button";
    faceBtn.className = "deck-face-study";
    faceBtn.title = `${verse.reference} — locked`;
    faceBtn.setAttribute("aria-label", `${verse.reference}, not yet owned`);
    const face = this.buildGameFace(verse, false);
    face.classList.add("is-locked-face");
    faceBtn.appendChild(face);
    // Allow study of locked cards so players can learn what they're chasing
    faceBtn.addEventListener("click", () => this.openStudy(verse));

    const blurb = document.createElement("p");
    blurb.className = "deck-card-blurb";
    blurb.textContent = "Earn in campaign · up to ×" + MAX_COPIES;

    const lockBtn = document.createElement("button");
    lockBtn.type = "button";
    lockBtn.className = "deck-select-btn is-locked";
    lockBtn.textContent = "Locked";
    lockBtn.disabled = true;
    lockBtn.title = "Win campaign stages to unlock this verse";

    wrap.append(faceBtn, blurb, lockBtn);
    return wrap;
  }

  /**
   * @param inLoadout — render as a loadout slot (Remove) vs catalog (Add)
   * @param occurrence — 0-based index among copies of this id in the loadout
   *   (only meaningful when inLoadout is true)
   */
  private renderCard(
    verse: CatalogVerse,
    inLoadout: boolean,
    occurrence = 0,
  ): HTMLElement {
    const wrap = document.createElement("div");
    wrap.className = "deck-card-slot" + (inLoadout ? " in-loadout" : "");
    wrap.setAttribute("role", "listitem");

    const owned = copiesOwned(verse.id);
    const inDeck = this.countInLoadout(verse.id);
    const free = Math.max(0, owned - inDeck);
    const deckFull = this.selected.length >= this.max;
    const atOwnedCap = free <= 0;

    if (inLoadout) wrap.classList.add("selected");
    else if (inDeck > 0) wrap.classList.add("partial-in-loadout");

    const faceBtn = document.createElement("button");
    faceBtn.type = "button";
    faceBtn.className = "deck-face-study";
    faceBtn.title = `Study ${verse.reference}`;
    faceBtn.setAttribute("aria-label", `Study ${verse.reference}`);
    const face = this.buildGameFace(verse, inLoadout || inDeck > 0);
    // Ownership / loadout multiplicity badges on the face
    if (owned > 1 || inDeck > 0) {
      const qty = document.createElement("span");
      qty.className = "deck-card-qty";
      if (inLoadout) {
        qty.textContent = owned > 1 ? `×${inDeck}/${owned}` : "×1";
        qty.title = `${inDeck} in loadout · ${owned} owned`;
      } else {
        qty.textContent = `×${owned}`;
        qty.title =
          free > 0
            ? `Owned ×${owned} · ${inDeck} in loadout · ${free} free`
            : `Owned ×${owned} · all in loadout`;
      }
      face.appendChild(qty);
    }
    faceBtn.appendChild(face);
    faceBtn.addEventListener("click", () => this.openStudy(verse));

    const blurb = document.createElement("p");
    blurb.className = "deck-card-blurb";
    if (inLoadout) {
      blurb.textContent =
        owned > 1
          ? `${verse.blurb} · ${inDeck} of ${owned} owned in loadout`
          : verse.blurb;
    } else {
      blurb.textContent =
        owned > 1
          ? `${verse.blurb} · Owned ×${owned}${inDeck > 0 ? ` · ${inDeck} in loadout` : ""}`
          : inDeck > 0
            ? `${verse.blurb} · in loadout`
            : verse.blurb;
    }

    const selectBtn = document.createElement("button");
    selectBtn.type = "button";

    if (inLoadout) {
      selectBtn.className = "deck-select-btn is-selected";
      selectBtn.textContent = "Remove";
      selectBtn.title = `Remove one ${verse.reference} from loadout`;
      selectBtn.setAttribute("aria-pressed", "true");
      selectBtn.setAttribute(
        "aria-label",
        `Remove one ${verse.reference} from loadout`,
      );
      selectBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        this.removeCopy(verse.id, occurrence);
      });
    } else {
      const canAdd = !deckFull && !atOwnedCap;
      selectBtn.className =
        "deck-select-btn" + (inDeck > 0 ? " is-partial" : "");
      if (!canAdd) {
        selectBtn.textContent = deckFull
          ? "Deck full"
          : owned > 1
            ? `Max ×${owned}`
            : "In loadout";
        selectBtn.disabled = true;
        selectBtn.title = deckFull
          ? `Maximum ${this.max} cards`
          : `You own ${owned} cop${owned === 1 ? "y" : "ies"} — all are in the loadout`;
      } else {
        selectBtn.textContent =
          owned > 1
            ? `Add (${inDeck + 1}/${owned})`
            : inDeck > 0
              ? "Add another"
              : "Add";
        selectBtn.title =
          owned > 1
            ? `Add another ${verse.reference} (${inDeck} of ${owned} owned in loadout)`
            : `Add ${verse.reference} to loadout`;
        selectBtn.setAttribute("aria-pressed", inDeck > 0 ? "true" : "false");
        selectBtn.setAttribute(
          "aria-label",
          `Add ${verse.reference} to loadout${owned > 1 ? ` (${free} of ${owned} free)` : ""}`,
        );
        selectBtn.addEventListener("click", (e) => {
          e.stopPropagation();
          this.addCopy(verse.id);
        });
      }
    }

    wrap.append(faceBtn, blurb, selectBtn);
    return wrap;
  }

  private clearStaleStatus(): void {
    const prev = this.actionStatusEl?.textContent ?? "";
    if (
      !prev ||
      prev.startsWith("Saved") ||
      prev.startsWith("Loaded") ||
      prev.startsWith("Created") ||
      prev.startsWith("Renamed") ||
      prev.startsWith("Deleted") ||
      (prev.startsWith("“") && prev.includes("already saved"))
    ) {
      this.setStatus("", false);
    }
  }

  private addCopy(verseId: string): void {
    if (!isOwned(verseId)) {
      this.setStatus("You do not own this verse yet.", true);
      return;
    }
    if (this.selected.length >= this.max) {
      this.setStatus(`Maximum ${this.max} cards.`, true);
      return;
    }
    const owned = copiesOwned(verseId);
    const inDeck = this.countInLoadout(verseId);
    if (inDeck >= owned) {
      this.setStatus(
        `You only own ${owned} cop${owned === 1 ? "y" : "ies"} of this verse.`,
        true,
      );
      return;
    }
    this.selected.push(verseId);
    this.clearStaleStatus();
    this.renderList();
    this.syncChrome();
  }

  /**
   * Remove one copy of verseId from the loadout.
   * `occurrence` is the 0-based index among that id’s copies (matches render order).
   */
  private removeCopy(verseId: string, occurrence = 0): void {
    let seen = 0;
    const idx = this.selected.findIndex((id) => {
      if (id !== verseId) return false;
      if (seen === occurrence) return true;
      seen++;
      return false;
    });
    if (idx < 0) {
      // Fallback: remove last matching copy
      const last = this.selected.lastIndexOf(verseId);
      if (last < 0) return;
      this.selected.splice(last, 1);
    } else {
      this.selected.splice(idx, 1);
    }
    this.clearStaleStatus();
    this.renderList();
    this.syncChrome();
  }
}
